import { useContext, useState } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Crown, Check, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Subscription = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to create checkout session');
      }
    } catch (err) {
      alert('Payment error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pt-24">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-16 h-16 bg-white border-2 border-teal rounded-2xl flex items-center justify-center">
              <Crown className="w-10 h-10 text-black" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold">Upgrade to Premium</h1>
          </div>
          <p className="text-gray-600 text-lg">Unlock exclusive features and connect without limits</p>
        </div>

        {user?.is_premium && (
          <div className="glass-dark rounded-3xl p-6 mb-8 text-center border-2 border-gold">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Crown className="w-6 h-6 text-gold" />
              <span className="text-xl font-bold text-gold">You're a Premium Member!</span>
            </div>
            <p className="text-gray-600">Enjoying all the exclusive features</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan */}
          <div className="glass-dark rounded-3xl p-8 shadow-lg">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Free</h3>
              <div className="text-4xl font-bold mb-4">$0<span className="text-lg text-gray-600">/forever</span></div>
              <p className="text-gray-600">Great for getting started</p>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm">Create and edit profile</span>
              </li>
              <li className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm">Browse users worldwide</span>
              </li>
              <li className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm">Unlimited connections</span>
              </li>
              <li className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm">5 messages per day</span>
              </li>
              <li className="flex items-start space-x-2">
                <Check className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                <span className="text-sm">Basic profile visibility</span>
              </li>
            </ul>

            <Button
              disabled={!user?.is_premium}
              className={`w-full py-6 rounded-xl font-semibold ${
                !user?.is_premium 
                  ? 'bg-white text-black border-2 border-teal cursor-not-allowed' 
                  : 'bg-gray-200 text-gray-600 cursor-default'
              }`}
            >
              {!user?.is_premium ? 'Current Plan' : 'Free'}
            </Button>
          </div>

          {/* Premium Yearly */}
          <div className="glass-dark rounded-3xl p-8 shadow-2xl border-4 border-gold relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-white text-black border-2 border-teal px-6 py-2 rounded-bl-3xl font-bold text-sm">
              BEST VALUE
            </div>
            
            <div className="text-center mb-6 mt-4">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Crown className="w-8 h-8 text-gold" />
                <h3 className="text-2xl font-bold">Premium Yearly</h3>
              </div>
              <div className="text-4xl font-bold mb-2">$24.99<span className="text-lg text-gray-600">/year</span></div>
              <p className="text-teal-600 font-semibold">Only $1.67 per month!</p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span><strong>Unlimited messaging</strong> with all users</span>
              </li>
              <li className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span><strong>AI-powered match recommendations</strong></span>
              </li>
              <li className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span><strong>Verified badge</strong> on your profile</span>
              </li>
              <li className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span><strong>Priority profile visibility</strong></span>
              </li>
              <li className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span><strong>Ad-free experience</strong></span>
              </li>
              <li className="flex items-start space-x-3">
                <Sparkles className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                <span><strong>Advanced filters</strong> for discovery</span>
              </li>
            </ul>

            {!user?.is_premium ? (
              <div className="space-y-4">
                <Button
                  data-testid="subscribe-yearly-btn"
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="w-full py-6 rounded-xl text-lg font-semibold bg-gold text-white hover:bg-gold-dark"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 inline animate-spin" /> Processing...</>
                  ) : (
                    <><Crown className="w-5 h-5 mr-2 inline" /> Subscribe Now - $24.99/year</>
                  )}
                </Button>
                <p className="text-xs text-center text-gray-600">
                  Best value! Just $1.67 per month when paid annually
                </p>
              </div>
            ) : (
              <Button
                disabled
                className="w-full py-6 rounded-xl font-semibold bg-white text-black border-2 border-gold cursor-default"
              >
                <Crown className="w-5 h-5 mr-2 inline text-gold" />
                Active Subscription
              </Button>
            )}
          </div>
        </div>

        {/* Payment Info Section */}
        {!user?.is_premium && (
          <div className="mt-8 glass-dark rounded-3xl p-6">
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-4 border-2 border-teal">
                <p className="text-sm font-semibold text-gray-700 mb-3 text-center">Accepted Payment Methods:</p>
                <div className="flex items-center justify-center space-x-4 flex-wrap gap-2">
                    <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg">
                      <svg className="w-8 h-6" viewBox="0 0 48 32" fill="none">
                        <rect width="48" height="32" rx="4" fill="#1434CB"/>
                        <circle cx="18" cy="16" r="8" fill="#EB001B"/>
                        <circle cx="30" cy="16" r="8" fill="#FF5F00" fillOpacity="0.8"/>
                      </svg>
                      <span className="text-xs font-medium">Mastercard</span>
                    </div>
                    <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg">
                      <svg className="w-8 h-6" viewBox="0 0 48 32" fill="none">
                        <rect width="48" height="32" rx="4" fill="#0066B2"/>
                        <path d="M20 10h8v12h-8z" fill="#FFA500"/>
                      </svg>
                      <span className="text-xs font-medium">Visa</span>
                    </div>
                    <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg">
                      <svg className="w-8 h-6" viewBox="0 0 48 32" fill="none">
                        <rect width="48" height="32" rx="4" fill="#016FD0"/>
                        <path d="M20 16l4-6h4l-4 6 4 6h-4l-4-6z" fill="#fff"/>
                      </svg>
                      <span className="text-xs font-medium">Amex</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">Secure payment processing by Stripe</p>
                </div>

                <div className="text-center">
                  <p className="text-xs text-gray-600">Secure payment with Credit/Debit Card</p>
                  <p className="text-xs text-center text-gray-500 mt-2">Cancel anytime. No hidden fees. Instant activation</p>
                </div>
              </div>
            </div>
        )}

        {/* Features Grid */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-8">Why Go Premium?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass rounded-3xl p-6 text-center">
              <div className="w-16 h-16 bg-white border-2 border-teal rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">Smart Matching</h3>
              <p className="text-gray-600">AI analyzes your profile to suggest the most compatible people</p>
            </div>
            <div className="glass rounded-3xl p-6 text-center">
              <div className="w-16 h-16 bg-white border-2 border-teal rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">Stand Out</h3>
              <p className="text-gray-600">Get a verified badge and priority visibility in searches</p>
            </div>
            <div className="glass rounded-3xl p-6 text-center">
              <div className="w-16 h-16 bg-white border-2 border-teal rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold mb-2">No Limits</h3>
              <p className="text-gray-600">Message anyone, anytime without restrictions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
